import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, LayoutGrid, LogOut, HardHat, Wrench, Settings,
  HelpCircle, MapPin, Package, AlertOctagon, Users,
  CalendarDays, ClipboardList, FileCheck, ShieldAlert, FileSignature,
  Building2, UserCog,
} from "lucide-react";
import { HelpPanel } from "@/components/help-panel";
import { NotificationBell } from "@/components/notification-bell";
import { Link, useLocation } from "wouter";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarHeader, SidebarFooter,
} from "@/components/ui/sidebar";
import { useUser } from "@/components/user-context";
import { useFeatures } from "@/lib/features";
import { isAdmin, isPlatformSuperadmin } from "@/lib/roles";
import { apiRequest } from "@/lib/queryClient";

// Phase 12 navigation — fire-safety operations IA.
//
// Four groups, in compliance-lifecycle order:
//   Overview    — the dashboard.
//   Compliance  — the entities that make this app a compliance system
//                 (customers, sites, assets, visits, calendar, defects,
//                  certificates, FRAs, contracts).
//   Commercial  — the few legacy commercial views that survive Phase 12.
//                 Empty by default; opt-in via the team flag.
//   Admin       — portal user management / settings.
//
// Recurring schedules are deliberately NOT a top-level sidebar entry.
// They are configuration that drives Visits and the Dashboard's "Due
// soon" rollup; setting cadence happens on Site / Asset detail pages,
// and auto-queued visits surface inside /visits with a "Recurring"
// badge. The settings flag for auto-queue lives on Settings → Operations.
//
// Items gated by feature flags stay hidden until their UI ships. Defaults
// for the new fire-safety modules live in client/src/lib/features.tsx.
const adminGroups = [
  {
    label: "Overview",
    items: [
      { title: "Dashboard", url: "/", icon: LayoutDashboard, featureFlag: undefined, exact: true },
    ],
  },
  {
    label: "Compliance",
    items: [
      // Customer / site / asset register — the data foundation.
      { title: "Customers",   url: "/customers",   icon: Building2,      featureFlag: undefined,                       exact: false },
      { title: "Sites",       url: "/sites",       icon: MapPin,         featureFlag: undefined,                       exact: false },
      { title: "Assets",      url: "/assets",      icon: Package,        featureFlag: "assets" as const,               exact: false },
      // Day-to-day work.
      { title: "Visits",      url: "/visits",      icon: ClipboardList,  featureFlag: undefined,                       exact: false },
      { title: "Calendar",    url: "/calendar",    icon: CalendarDays,   featureFlag: "schedule" as const,             exact: false },
      { title: "Defects",     url: "/defects",     icon: AlertOctagon,   featureFlag: "defects" as const,              exact: false },
      // Outputs.
      { title: "Certificates", url: "/certificates", icon: FileCheck,    featureFlag: "certificates" as const,         exact: false },
      { title: "Risk Assessments", url: "/fras", icon: ShieldAlert,      featureFlag: "riskAssessments" as const,      exact: false },
      { title: "Contracts",   url: "/contracts",   icon: FileSignature,  featureFlag: "contracts" as const,            exact: false },
    ],
  },
  {
    label: "Commercial",
    items: [
      // Team management is the only commercial-ish view that stays — engineers
      // need to be assigned to visits. Quotes/Invoices/Payments retired in Phase 12.
      { title: "Team", url: "/engineers", icon: Users, featureFlag: "team" as const, exact: false },
    ],
  },
  {
    label: "Admin",
    items: [
      // Customer-facing portal user management (ADR 0002 — slice 8).
      // Always visible to admins; the page itself includes a customer
      // picker so admins can manage portal users for any customer.
      { title: "Portal Users", url: "/portal-users", icon: UserCog,   featureFlag: undefined, exact: false },
      { title: "Settings",     url: "/settings",     icon: Settings, featureFlag: undefined, exact: false },
    ],
  },
];

const engineerItems = [
  { title: "My Jobs", url: "/", icon: LayoutGrid, featureFlag: undefined },
];

export function AppSidebar() {
  const { currentUser, logout } = useUser();
  const features = useFeatures();
  const [location, navigate] = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);

  const { data: settings } = useQuery<{ companyName?: string }>({
    queryKey: ["/api/settings"],
    queryFn: () => apiRequest("GET", "/api/settings").then(r => r.json()),
    staleTime: 300_000,
  });

  const userIsAdmin = isAdmin(currentUser);
  const isSuperadmin = isPlatformSuperadmin(currentUser);

  return (
    <>
    <Sidebar>
      <SidebarHeader className="px-4 py-4 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            {/* Logo mark — shield + tick, BS 5499 emergency-exit green */}
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-label="FireSafetyOps logo" className="shrink-0">
              <polygon points="4,5 28,5 28,18 16,30 4,18" fill="#00843D" />
              <polygon points="10,17 14.5,21.5 22,12 19.5,9.8 14.2,16.2 12,14" fill="#ffffff" />
            </svg>
            <div className="leading-tight">
              <span className="font-semibold text-sidebar-foreground text-sm tracking-tight">FireSafetyOps</span>
              {settings?.companyName && (
                <p className="text-xs text-sidebar-foreground/65">{settings.companyName}</p>
              )}
            </div>
          </Link>
          <NotificationBell />
        </div>
        {/* Quick-action buttons removed in Phase 12. New visit / new defect /
            new customer entry points live inside their respective pages. */}
      </SidebarHeader>

      <SidebarContent>
        {userIsAdmin ? (
          // Admin: grouped navigation
          adminGroups.map(group => {
            const visibleItems = group.items.filter(item =>
              item.featureFlag === undefined || features[item.featureFlag]
            );
            if (visibleItems.length === 0) return null;
            return (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel className="text-xs uppercase tracking-widest text-sidebar-foreground/60 px-3 py-2">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleItems.map(item => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={
                            item.exact
                              ? location === item.url
                              : location === item.url || location.startsWith(item.url + "/")
                          }
                          data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Link href={item.url} className="flex items-center gap-2.5">
                            <item.icon className="w-4 h-4 shrink-0" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })
        ) : (
          // Engineer: single flat list
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-widest text-sidebar-foreground/60 px-3 py-2">
              Engineer
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {engineerItems.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.url}
                      data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Link href={item.url} className="flex items-center gap-2.5">
                        <item.icon className="w-4 h-4 shrink-0" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3 pb-2">
        <div className="flex items-center gap-2 mb-2 px-1">
          <div className="w-7 h-7 rounded-full bg-sidebar-primary/20 flex items-center justify-center shrink-0">
            {userIsAdmin ? (
              <Wrench className="w-3.5 h-3.5 text-sidebar-primary" />
            ) : (
              <HardHat className="w-3.5 h-3.5 text-sidebar-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-sidebar-foreground truncate">{currentUser?.name}</p>
            {isSuperadmin ? (
              <p
                className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 truncate"
                title="Logged in as Cushty platform support"
              >
                Platform support
              </p>
            ) : (
              <p className="text-xs text-sidebar-foreground/70 capitalize">{currentUser?.role}</p>
            )}
          </div>
        </div>
        <SidebarMenuButton
          onClick={() => setHelpOpen(true)}
          data-testid="button-help-open"
          className="w-full text-sidebar-foreground/80 hover:text-sidebar-foreground"
        >
          <HelpCircle className="w-4 h-4" />
          <span>Help &amp; Feedback</span>
        </SidebarMenuButton>
        <SidebarMenuButton
          onClick={() => { navigate("/"); logout(); }}
          data-testid="button-logout"
          className="w-full text-sidebar-foreground/80 hover:text-sidebar-foreground"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign out</span>
        </SidebarMenuButton>
        <p className="text-center text-[10px] text-sidebar-foreground/35 pt-2 pb-0.5 tracking-wide">
          Powered by Cushty.io
        </p>
      </SidebarFooter>
    </Sidebar>
    <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} role={currentUser?.role as "admin" | "engineer" | undefined} />
    </>
  );
}
